import { DateFormatter, DurationFormatter } from '@/shared/ui/components';
import { DEVICE, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { DeviceIdFormatter, checkIcorDevice, isEndOfLife } from '@/entities/device';
import {
  STUDY_STATUS,
  C1cDeviceLabel,
  PatientNameFormatter,
  StudyFidFormatter,
  checkUsingHighSamplingRateStudy,
  isC1cStudy,
  isManagedC1cDevice,
} from '@/entities/study';
import { convertToMoment, formatInitialDuration, gettextStudyType } from '@/shared/lib';
import { checksIsHoliday, getHolidayDuration, getHolidays } from '@/shared/lib/holidays';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import ColumnWithActionFormatter from '../ui/studiesTable/columnWithActionFormatter/columnWithActionFormatter';
import type { StudyFormatterArg } from './studiesList.types';

dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);

/**
 * Renders the study FID column as a clickable link opening the study information drawer with the Study information title. Forwards the artifact-report and Bioflux Direct flags plus deleted and aborted state so the formatter can badge the id accordingly, and shows a skeleton while isLoadedData is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The study FID formatter element, or a loading skeleton.
 */
const studyFidFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { isDeleted, toggleDisplayDrawer, isLoadedData } = extraData;
  if (isLoadedData) {
    return (
      <StudyFidFormatter
        studyId={data}
        handleClickStudyId={() => {
          toggleDisplayDrawer(record, t`Study information`);
        }}
        hasArtifactReport={record.hasArtifactReport}
        isBiofluxDirect={record.isBiofluxDirect}
        isDeleted={isDeleted}
        isAborted={record.status === STUDY_STATUS.ABORTED}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the ETA report date column through the shared date formatter, localised to the study timezone and parsed as a dayjs value. Shows a single-line skeleton while isLoadedData is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The formatted ETA report date, or a loading skeleton.
 */
const etaReportDateFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { isLoadedData } = extraData;
  return isLoadedData ? (
    <DateFormatter date={data} timezone={record.timezone} isDayjs />
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the study stop time as a date and time broken across lines, supplying the study start, timezone, time offset and the facility's country, timezone and record so the formatter can annotate timezone differences. Emits a double-hyphen bullet when no stop time exists and a skeleton while isLoadedData is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The formatted stop time, a placeholder, or a loading skeleton.
 */
const stopTimeFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { isLoadedData } = extraData;
  return isLoadedData ? (
    data ? (
      <DateFormatter
        startStudyDate={record.start}
        date={data}
        timezone={record.timezone}
        studyTimeOffset={record.studyTimeOffset}
        facilityCountry={record.facility?.contact?.country}
        facilityTimezone={record.facility?.timezone}
        facility={record.facility}
        format="MM/DD/YYYY HH:mm:ss"
        isBreakDayAndTime
      />
    ) : (
      DOUBLE_HYPHEN_BULLET
    )
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the patient return visit column as a UTC date. On the ongoing and flagged-studies tabs a linked follow-on study takes precedence and its reportDueDate is parsed as a UTC YYYY-MM-DD value, otherwise the study's own reportDueDate is shown; a skeleton is rendered while isLoadedData is false.
 *
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The formatted return visit date, or a loading skeleton.
 */
const patientReturnVisitFormatter = ({ record, extraData }: StudyFormatterArg) => {
  const { isOngoing, isFlaggedStudiesTab, isLoadedData } = extraData;
  return isLoadedData ? (
    <DateFormatter
      date={
        (isOngoing || isFlaggedStudiesTab) && !_.isEmpty(record.followOnStudy)
          ? dayjs.utc(record.followOnStudy?.reportDueDate, 'YYYY-MM-DD')
          : record.reportDueDate
      }
      timezone="UTC+0"
    />
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the study duration column, optionally overlaying a progress bar for studies still running. Treats a last history status of UPLOADING or UPLOADED as upload progress and then reports ecgUploadProgress instead of the study progress, hides the bar entirely for completed and deleted tabs, and shows a skeleton while isLoadedData is false.
 *
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The duration formatter element, or a loading skeleton.
 */
const durationFormatter = ({ record, extraData }: StudyFormatterArg) => {
  const { isCompleted, isDeleted, isLoadedData } = extraData;
  if (isLoadedData) {
    const isUploadProgress = record.lastStudyHistory
      ? _.includes([STUDY_STATUS.UPLOADING, STUDY_STATUS.UPLOADED], record.lastStudyHistory.status)
      : false;
    const progressValue = isUploadProgress ? record.ecgUploadProgress : record.progress;
    return (
      <DurationFormatter
        duration={record.duration}
        isShowProgress={!isCompleted && !isDeleted}
        progressValue={progressValue}
        isUploadProgress={isUploadProgress}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the initial prescribed duration of a study, humanised by formatInitialDuration. Falls back to a double-hyphen bullet when the formatter yields nothing and to a skeleton while isLoadedData is false.
 *
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns A span with the initial duration, or a loading skeleton.
 */
const initDurationFormatter = ({ record, extraData }: StudyFormatterArg) => {
  const { isLoadedData } = extraData;
  if (isLoadedData) {
    return (
      <span>{formatInitialDuration(record.initialDurationOfStudy) || DOUBLE_HYPHEN_BULLET}</span>
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the patient name column of the studies table as a clickable link that opens the patient information drawer for that study. Passes the record and the Patient information title to toggleDisplayDrawer, and shows a skeleton while isLoadedData is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The patient name formatter element, or a loading skeleton.
 */
const patientNameFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { toggleDisplayDrawer, isLoadedData } = extraData;
  if (isLoadedData) {
    return (
      <PatientNameFormatter
        patientName={data}
        handleClickPatientName={() => {
          toggleDisplayDrawer(record, t`Patient information`);
        }}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the device id column of the studies table. An iCor device shows the id next to a static iCor label, a managed CardioOne LifeSignals or VivaLNK device shows the id as plain text, any other device renders the clickable device id formatter, both followed by the CardioOne device label for LifeSignals and VivaLNK studies, flags end-of-life from the device's first study date and high sampling rate from the study's ECG detail; a skeleton is shown until isLoadedData is true.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The device id cell, or a loading skeleton.
 */
const deviceIdFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { toggleDisplayDrawer, isLoadedData } = extraData;

  if (isLoadedData) {
    const isIcorDevice = checkIcorDevice(record.device);
    const isManagedC1cDeviceStudy = isManagedC1cDevice(isC1cStudy(record), record.deviceType);
    if (isIcorDevice) {
      return (
        <div className="icor-device-container">
          <span className="icor-device-id">{data}</span>
          <span className="icor-device-label">{DEVICE.ICOR}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center flex-wrap" style={{ columnGap: 8 }}>
        {isManagedC1cDeviceStudy ? (
          <span className="nowrap">{data}</span>
        ) : (
          <DeviceIdFormatter
            deviceId={data}
            isEOL={isEndOfLife(record.device?.firstStudyDate)}
            handleClickDeviceId={() => {
              toggleDisplayDrawer(record, t`Device information`);
            }}
            isHighSamplingRate={checkUsingHighSamplingRateStudy(record.ecgDetail)}
          />
        )}
        <C1cDeviceLabel deviceType={record.deviceType} />
      </div>
    );
  }

  return <Skeleton count={1} />;
};

/**
 * Renders the study type column with its translated label and a Follow-on badge for completed studies that have linked studies or a follow-on study. Renders a plain inline layout when the facility name column is shown and otherwise the column-with-action formatter keyed by the record's actionButtonTag; a skeleton is shown while isLoadedData is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param record - The study record the cell belongs to.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns The study type cell, or a loading skeleton.
 */
const studyTypeFormatter = ({ data, record, extraData }: StudyFormatterArg) => {
  const { isCompleted, isShowFacilityName, isLoadedData } = extraData;
  if (isLoadedData) {
    if (isShowFacilityName) {
      return (
        <div className="flex items-center flex-wrap">
          <span>{gettextStudyType(data)}</span>
          {isCompleted &&
            (record.linkedStudies?.length > 0 || !_.isEmpty(record.followOnStudy)) && (
              <div className="follow-on-study">{t`Follow-on`}</div>
            )}
        </div>
      );
    }
    return (
      <ColumnWithActionFormatter
        rowIndex={record.actionButtonTag}
        text={gettextStudyType(data)}
        isFollowOnStudy={
          isCompleted && (record.linkedStudies?.length > 0 || !_.isEmpty(record.followOnStudy))
        }
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders a plain text cell of the studies table from the supplied data value. Falls back to a single-line skeleton while the table's isLoadedData flag is false.
 *
 * @param data - The raw value of the cell being rendered.
 * @param extraData - Extra table context carrying isLoadedData, tab flags and the drawer toggler.
 * @returns A span with the cell text, or a loading skeleton.
 */
const cellFormatter = ({ data, extraData }: StudyFormatterArg) => {
  const { isLoadedData } = extraData;
  return isLoadedData ? <span>{data}</span> : <Skeleton count={1} />;
};

/**
 * Computes the ETA report date of a study, which is four business days after the study was erased. Returns a double-hyphen bullet unless the last study history status is ERASED, then adds four business days in the study timezone and extends the date again by the duration of any facility holiday that falls in the range and lands on a business day, repeating the extension when the resulting date is itself a holiday.
 *
 * @param study - The study whose ETA report date is derived from its erase history and facility holidays.
 * @returns The ETA report date as a moment, or a double-hyphen bullet placeholder.
 */
const calculateEtaReportDate = (study: any): any => {
  let etaReportDate: any = DOUBLE_HYPHEN_BULLET;
  const studyTimezone = study.timezone;
  const erasedStudyDate: any =
    study.lastStudyHistory?.status === STUDY_STATUS.ERASED
      ? convertToMoment({
          isoDate: study.lastStudyHistory.time,
          timezone: studyTimezone,
        })
      : null;
  if (erasedStudyDate) {
    // *: ETA report date is 4 business days after Erased study date (exclude holidays)
    etaReportDate = erasedStudyDate.businessDaysAdd(4, 'days');
    const facilityTimezone = study.facility?.timezone;
    const holidays = getHolidays({
      timezone: facilityTimezone,
    });
    if (holidays.length) {
      // *: Find holidays in range of Erased study date and ETA report date (only accept for business day)
      // *: Holiday is a weekend -> no need to add more day (because of holidays will add more substitute day to array)
      // *: Holiday is a business day -> add more day
      /**
       * The facility holidays that fall between the erased study date and the current ETA report date and land on a business day. Filtered from the holidays list by comparing holiday.start against both dates and testing the study-timezone moment with isBusinessDay.
       *
       * @returns The subset of holidays that extend the ETA report date.
       */
      const includedBusinessHolidays = _.filter(
        holidays,
        (holiday) =>
          holiday.start >= erasedStudyDate.clone().toDate() &&
          holiday.start <= etaReportDate.clone().toDate() &&
          (
            convertToMoment({
              isoDate: holiday.start as any,
              timezone: studyTimezone,
            }) as any
          ).isBusinessDay(),
      );
      if (includedBusinessHolidays.length) {
        const holidayDuration = getHolidayDuration(includedBusinessHolidays);
        etaReportDate = etaReportDate.businessDaysAdd(holidayDuration, 'days');
        const isHoliday = checksIsHoliday({
          date: etaReportDate,
          timezone: facilityTimezone,
        });
        if (isHoliday) {
          // *: New ETA report date is a holiday (continue to add holiday duration)
          const inDayHolidayDuration = getHolidayDuration(isHoliday);
          etaReportDate = etaReportDate.businessDaysAdd(inDayHolidayDuration, 'days');
        }
      }
    }
  }
  return etaReportDate;
};

export {
  calculateEtaReportDate,
  cellFormatter,
  deviceIdFormatter,
  durationFormatter,
  etaReportDateFormatter,
  initDurationFormatter,
  patientNameFormatter,
  patientReturnVisitFormatter,
  stopTimeFormatter,
  studyFidFormatter,
  studyTypeFormatter,
};
